import type { BrowserWindow, ILogger, Experiments } from '@wix/thunderbolt-symbols'

export const isMobileUserAgent = (window: BrowserWindow) => {
	return /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window!.navigator.userAgent)
}

export const detectRetinaScreen = (window: BrowserWindow) => {
	if (!window || !window.devicePixelRatio || !window.innerWidth || !window.outerWidth) {
		return 0
	}

	return Math.trunc(window.devicePixelRatio * window.innerWidth) <= window.outerWidth ? 1 : 2
}

export const detectBrowserZoomDesktop = (window: BrowserWindow) => {
	if (!window) {
		return 1
	}

	const displayDPR = detectRetinaScreen(window)
	if (!displayDPR) {
		return 1
	}
	const dpr = window.devicePixelRatio

	// ignore zoom out
	if (dpr <= displayDPR) {
		return 1
	}

	return Math.round((dpr / displayDPR) * 100) / 100
}

export const detectBrowserZoomOnMobile = (window: BrowserWindow) => {
	if (!window) {
		return 1
	}

	return window.visualViewport?.scale || 1
}

export const hasBrowserZoom = (window: BrowserWindow) => {
	if (!window) {
		return false
	}

	const isMobile = isMobileUserAgent(window)
	const browserZoom = isMobile ? detectBrowserZoomOnMobile(window) : detectBrowserZoomDesktop(window)
	return browserZoom > 1 ? true : false
}

/**
 * Freeze all font-size values to their computed pixel values
 * This makes fonts respond to browser zoom but not screen size changes
 */
export const freezeFontsToPixelValues = (window: BrowserWindow, logger: ILogger): number => {
	logger.interactionStarted('freeze_fonts_for_browser_zoom')

	const allElements = window!.document.querySelectorAll(
		'body *:not(script):not(style):not(svg):not(path):not(g):not(defs):not(clipPath)'
	)

	// Batch read computed styles (avoid layout thrashing)
	const computedStyles: Array<CSSStyleDeclaration> = []
	allElements.forEach((element) => {
		computedStyles.push(window!.getComputedStyle(element))
	})

	let frozenCount = 0

	// Batch write inline styles
	allElements.forEach((element, i) => {
		const style = computedStyles[i]
		const fontSize = style.fontSize

		if (fontSize && fontSize !== '0px') {
			;(element as HTMLElement).style.fontSize = fontSize
			frozenCount++
		}

		const lineHeight = style.lineHeight
		if (lineHeight && lineHeight !== 'normal') {
			;(element as HTMLElement).style.lineHeight = lineHeight
		}
	})

	logger.interactionEnded('freeze_fonts_for_browser_zoom', {
		customParams: {
			frozenElements: frozenCount,
			totalElements: allElements.length,
		},
	})

	return frozenCount
}

/**
 * Setup zoom listeners for runtime detection
 */
export const setupZoomListeners = (window: BrowserWindow, onZoomDetected: () => void): (() => void) | null => {
	const isMobile = isMobileUserAgent(window)

	if (isMobile) {
		// Mobile: listen to visualViewport scale changes
		const handleMobileZoom = () => {
			const scale = window!.visualViewport?.scale || 1
			if (scale > 1) {
				onZoomDetected()
			}
		}
		window!.visualViewport?.addEventListener('resize', handleMobileZoom, { once: true })

		// Return cleanup function
		return () => {
			window!.visualViewport?.removeEventListener('resize', handleMobileZoom)
		}
	} else {
		// Desktop: listen to devicePixelRatio changes
		const initialDPR = window!.devicePixelRatio || 1
		const mediaQuery = window!.matchMedia(`(resolution: ${initialDPR}dppx)`)

		const handleDesktopZoom = () => {
			const currentZoom = detectBrowserZoomDesktop(window)
			if (currentZoom > 1) {
				onZoomDetected()
			}
		}

		mediaQuery.addEventListener('change', handleDesktopZoom, { once: true })

		// Return cleanup function
		return () => {
			mediaQuery.removeEventListener('change', handleDesktopZoom)
		}
	}
}

/**
 * Browser Zoom Font Freezing - Main coordinator function
 *
 * Simple version: Setup listeners and freeze fonts when zoom detected
 * Works for the case: page loads at 100%, then user zooms in
 *
 * @param window - Browser window object
 * @param experiments - Experiments object for feature flag
 * @param logger - Logger for FedOps performance tracking
 * @returns Cleanup function to remove event listeners, or null if no cleanup needed
 */
export const freezeFontSizesOnZoom = (
	window: BrowserWindow,
	experiments: Experiments,
	logger: ILogger
): (() => void) | null => {
	const browserZoomHandler = experiments['specs.thunderbolt.browserZoomHandler']
	if (!browserZoomHandler) {
		return null
	}

	// Setup listeners and freeze fonts when zoom is detected
	return setupZoomListeners(window, () => {
		freezeFontsToPixelValues(window, logger)
	})
}
